import { useEffect, useState } from 'react';
import { getBlob, ApiError } from '../../lib/api.ts';
import { isNativeApp, saveFileNative } from '../../lib/platform.ts';
import { Alert, Modal, Spinner } from '../../components/ui.tsx';

/**
 * عارض ملف محمي: المسار يتطلب ترويسة Authorization، فلا يصلح كـsrc مباشر.
 * نجلب الملف كـBlob ونعرضه من عنوان مؤقت، ونحرره عند الإغلاق.
 *
 * داخل تطبيق أندرويد (Capacitor) يختلف أمران تقنيّان لا غير، وكلاهما محصور هنا:
 *   · الـWebView لا يعرض PDF داخل الصفحة (لا عارض مضمَّن) ولا يفتح رابط blob في «تبويب جديد» —
 *     فبدل زرٍّ ميت يُكتب الملف في مساحة التطبيق الخاصة ثم تُفتح ورقة المشاركة ليفتحه المستخدم
 *     بعارض النظام أو يرسله (نفس مسار تصدير Excel — قرار المالك D-6).
 *   · الصور تُعرض كما هي في الحالتين.
 * المحتوى واحد دائمًا: ما أرسله الخادم حرفيًا، بلا تعديل.
 */
export function FileViewer({ path, fileName, mimeType, onClose }:
  { path: string; fileName: string; mimeType: string; onClose: () => void }) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    let alive = true; let created: string | null = null;
    getBlob(path)
      .then(b => {
        if (!alive) return;
        created = URL.createObjectURL(b);
        setBlob(b);
        setUrl(created);
      })
      .catch(err => { if (alive) setError(err instanceof ApiError ? err.message : 'تعذّر فتح الملف'); });
    return () => { alive = false; if (created) URL.revokeObjectURL(created); };
  }, [path]);

  const isImage = mimeType.startsWith('image/');
  const native = isNativeApp();

  async function share(): Promise<void> {
    if (!blob) return;
    setSharing(true); setError(null);
    try { await saveFileNative(blob, fileName); }
    catch { setError('تعذّر فتح الملف على الجهاز'); }
    finally { setSharing(false); }
  }

  return (
    <Modal title={fileName} onClose={onClose}>
      {error && <Alert>{error}</Alert>}
      {!error && !url && <Spinner label="جارٍ فتح الملف…" />}
      {url && (
        <div className="space-y-3">
          {isImage
            ? <img src={url} alt={fileName} className="mx-auto max-h-[70vh] w-auto max-w-full rounded-lg border border-slate-200" />
            : native
              ? <Alert kind="info">ملفات PDF تُفتح بعارض الجهاز — اضغط «فتح / مشاركة الملف».</Alert>
              : <object data={url} type={mimeType} className="h-[70vh] w-full rounded-lg border border-slate-200" aria-label={fileName}>
                  <p className="p-4 text-sm text-ink-500">لا يمكن عرض الملف داخل الصفحة — استخدم زر الفتح بالأسفل.</p>
                </object>}
          {native
            ? <button type="button" className="btn-ghost" disabled={sharing} onClick={() => void share()}>
                {sharing ? 'جارٍ التحضير…' : 'فتح / مشاركة الملف'}
              </button>
            : <a href={url} target="_blank" rel="noreferrer" className="btn-ghost inline-flex">فتح في تبويب جديد</a>}
        </div>
      )}
    </Modal>
  );
}
